import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Grid, Typography, Progress } from 'antd';
import { ExportOutlined, RightOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetDashboardDataMutation } from '../../redux-toolkit/services/dashboardService';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PassionITScoreDashboard = () => {
    const screens = useBreakpoint();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // API Hooks
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const [getDashboardData, { data: dashboardData, isLoading: isDashboardLoading }] = useGetDashboardDataMutation();

    useEffect(() => {
        if (token) {
            findAllEntities({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    useEffect(() => {
        if (selectedEntity && token) {
            getDashboardData({ payload: { entityId: selectedEntity }, token });
        }
    }, [selectedEntity, token]);

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    // Derived Data
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // Mock Data for PassionIT Scores
    const heirScores = [
        {
            key: '1',
            name: 'Mukesh Ambani',
            role: 'Chairman / Founder',
            overallScore: 4.8,
            scores: {
                leadership: 5,
                innovation: 5,
                execution: 5,
                vision: 5
            }
        },
        {
            key: '2',
            name: 'Nita Ambani',
            role: 'Philanthropy / CSR',
            overallScore: 4.6,
            scores: {
                leadership: 5,
                innovation: 5,
                execution: 5,
                vision: 5
            }
        },
        {
            key: '3',
            name: 'Akash Ambani',
            role: 'Jio / Telecom',
            overallScore: 4.0,
            scores: {
                leadership: 5,
                innovation: 5,
                execution: 5,
                vision: 5
            }
        },
        {
            key: '4',
            name: 'Isha Ambani',
            role: 'Retail & Commerce',
            overallScore: 4.2,
            scores: {
                leadership: 5,
                innovation: 5,
                execution: 5,
                vision: 5
            }
        },
        {
            key: '5',
            name: 'Anant Ambani',
            role: 'New Energy',
            overallScore: 3.2,
            scores: {
                leadership: 5,
                innovation: 3,
                execution: 5,
                vision: 5
            }
        }
    ];

    const renderScoreBadge = (score: number) => {
        let color = '#52c41a'; // Green for high scores
        if (score < 4) color = '#1890ff'; // Blue
        if (score < 3) color = '#faad14'; // Yellow/Orange
        if (score < 2) color = '#f5222d'; // Red

        return (
            <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: color,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: '14px',
                margin: '0 auto'
            }}>
                {score}
            </div>
        );
    };

    // Sidebar Mock Data
    const alerts = [
        { id: 1, text: 'Project at risk: Retail OmniPilot (Isha Ambani) — requires attention.', type: 'warning' },
        { id: 2, text: 'Project at risk: Green Hydrogen Pilot (Anant Ambani) — requires attention.', type: 'warning' },
    ];
    const quickStats = [
        { label: 'Heirs', value: 5, color: '#e6f7ff', textColor: '#1890ff' },
        { label: 'Assets', value: 4, color: '#f6ffed', textColor: '#52c41a' },
        { label: 'Projects', value: 3, color: '#fff7e6', textColor: '#fa8c16' },
        { label: 'Audits', value: 3, color: '#fff1f0', textColor: '#f5222d' },
    ];

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        PASSIONIT — Reliance Family Office Prototype
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">PassionIT Score</span>
                    </div>
                </div>
                <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div>
            </div>

            {/* Main Content */}
            <Row gutter={[24, 24]}>
                {/* Left Column - PassionIT Scores */}
                <Col span={24}>
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <Title level={4} className="mb-4">PASSIONIT Universal Score</Title>

                        <Text type="secondary" className="mb-3 d-block">Showing 5 of 5 heirs</Text>

                        {/* Score Cards for Each Heir */}
                        {heirScores.map((heir) => (
                            <Card
                                key={heir.key}
                                className="mb-3"
                                bordered={false}
                                style={{ backgroundColor: '#fafafa' }}
                            >
                                <Row gutter={16} align="middle">
                                    <Col span={4}>
                                        <div>
                                            <Text strong style={{ display: 'block', fontSize: '16px' }}>{heir.name}</Text>
                                            <Text type="secondary" style={{ fontSize: '12px' }}>{heir.role}</Text>
                                        </div>
                                    </Col>
                                    <Col span={3}>
                                        {renderScoreBadge(heir.scores.leadership)}
                                    </Col>
                                    <Col span={3}>
                                        {renderScoreBadge(heir.scores.innovation)}
                                    </Col>
                                    <Col span={3}>
                                        {renderScoreBadge(heir.scores.execution)}
                                    </Col>
                                    <Col span={3}>
                                        {renderScoreBadge(heir.scores.vision)}
                                    </Col>
                                    <Col span={4}>
                                        <div style={{ textAlign: 'center' }}>
                                            <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>{heir.overallScore}</Text>
                                            <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>Overall Score</Text>
                                        </div>
                                    </Col>
                                    <Col span={4}>
                                        <a href="#" style={{ fontSize: '12px' }}>View Detailed Analysis →</a>
                                    </Col>
                                </Row>
                            </Card>
                        ))}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default PassionITScoreDashboard;
